import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, CheckCircle2, Clock, Star, StarOff, Trash2, Edit,
  Zap, ListTodo, Loader2, MoreVertical, CalendarDays, Sparkles, GripVertical, Bell, BellOff
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configurations with vibrant colors - darker backgrounds for better text contrast
const STATUSES = {
  urgent: { 
    label: 'Urgent', 
    color: 'bg-gradient-to-r from-red-500 to-rose-500', 
    lightColor: 'bg-red-50 border-red-300 text-red-700',
    cardBg: 'bg-white border-red-300',
    textColor: 'text-gray-900',
    icon: Zap,
  },
  todo: { 
    label: 'To Do', 
    color: 'bg-gradient-to-r from-blue-500 to-indigo-500', 
    lightColor: 'bg-blue-50 border-blue-300 text-blue-700',
    cardBg: 'bg-white border-blue-300',
    textColor: 'text-gray-900',
    icon: ListTodo,
  },
  on_hold: { 
    label: 'On Hold', 
    color: 'bg-gradient-to-r from-amber-500 to-yellow-500', 
    lightColor: 'bg-amber-50 border-amber-300 text-amber-700',
    cardBg: 'bg-white border-amber-300',
    textColor: 'text-gray-900',
    icon: Clock,
  },
  completed: { 
    label: 'Completed', 
    color: 'bg-gradient-to-r from-green-500 to-emerald-500', 
    lightColor: 'bg-green-50 border-green-300 text-green-700',
    cardBg: 'bg-white border-green-300',
    textColor: 'text-gray-500',
    icon: CheckCircle2,
  }
};

// Sortable Todo Item Component
function SortableTodoItem({ todo, onToggleComplete, onToggleStar, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: todo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const status = STATUSES[todo.status] || STATUSES.todo;
  const StatusIcon = status.icon;
  const isOverdue = todo.due_date && new Date(todo.due_date) < new Date() && todo.status !== 'completed';

  return (
    <div ref={setNodeRef} style={style}>
      <Card className={`${status.cardBg} ${status.textColor} !text-gray-900 border shadow-sm hover:shadow-md transition-all ${isDragging ? 'ring-2 ring-[#DC2626] shadow-lg' : ''}`}>
        <CardContent className="p-3">
          <div className="flex items-start gap-2">
            {/* Drag Handle */}
            <button
              {...attributes}
              {...listeners}
              className="mt-1 flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none"
              data-testid={`drag-handle-${todo.id}`}
            >
              <GripVertical className="w-4 h-4" />
            </button>

            {/* Checkbox */}
            <button
              onClick={() => onToggleComplete(todo)}
              className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                todo.status === 'completed' 
                  ? 'bg-green-500 border-green-500 text-white' 
                  : 'border-gray-400 hover:border-gray-600 bg-white'
              }`}
            >
              {todo.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
            </button>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <span className="font-bold text-sm leading-tight [color:#111827]">
                  {todo.title}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => onToggleStar(todo)}
                    className={`${todo.starred ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-500'}`}
                  >
                    {todo.starred ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                        <MoreVertical className="w-3 h-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(todo)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onToggleComplete(todo)}>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        {todo.status === 'completed' ? 'Reopen' : 'Complete'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => onDelete(todo.id)} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                <Badge className={`${status.lightColor} border text-xs px-1.5 py-0`}>
                  <StatusIcon className="w-2.5 h-2.5 mr-0.5" />
                  {status.label}
                </Badge>
                {todo.due_date && (
                  <Badge variant="outline" className={`text-xs px-1.5 py-0 ${isOverdue ? 'border-red-400 text-red-600 bg-red-50' : ''}`}>
                    <CalendarDays className="w-2.5 h-2.5 mr-0.5" />
                    {new Date(todo.due_date).toLocaleDateString()}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function DashboardTodoSection() {
  const { token } = useAuth();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [reminderTodo, setReminderTodo] = useState(null); // todo being scheduled
  const [reminderAt, setReminderAt] = useState('');        // datetime-local value
  const [editingTodo, setEditingTodo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'urgent',
    due_date: '',
    due_time: '',
    starred: false
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchTodos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/my-todos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTodos(data.todos || []);
        setStats(data.stats || {});
      }
    } catch (error) {
      console.error('Failed to fetch todos:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  // Filter todos (but maintain order from server)
  const filteredTodos = todos.filter(todo => {
    if (statusFilter === 'all') return todo.status !== 'completed';
    return todo.status === statusFilter;
  });

  // Handle drag end - reorder todos
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = filteredTodos.findIndex(t => t.id === active.id);
    const newIndex = filteredTodos.findIndex(t => t.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;

    // Optimistically update UI
    const newFilteredOrder = arrayMove(filteredTodos, oldIndex, newIndex);
    
    // Update the full todos array while preserving order
    const newTodos = [...todos];
    const filteredIds = new Set(filteredTodos.map(t => t.id));
    
    // Remove filtered items from their original positions
    const nonFiltered = newTodos.filter(t => !filteredIds.has(t.id));
    
    // Create new array with reordered filtered items at the beginning
    setTodos([...newFilteredOrder, ...nonFiltered]);

    // Save order to backend
    try {
      const todoIds = newFilteredOrder.map(t => t.id);
      await fetch(`${API_URL}/api/my-todos/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ todo_ids: todoIds })
      });
    } catch (error) {
      console.error('Failed to save order:', error);
      // Revert on error
      fetchTodos();
    }
  };

  const handleCreate = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/my-todos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('To-do added!');
        setShowAddModal(false);
        setFormData({ title: '', description: '', status: 'urgent', due_date: '', due_time: '', starred: false });
        fetchTodos();
      } else {
        toast.error('Failed to add to-do');
      }
    } catch (error) {
      toast.error('Failed to add to-do');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/my-todos/${editingTodo.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('To-do updated!');
        setShowEditModal(false);
        setEditingTodo(null);
        fetchTodos();
      } else {
        toast.error('Failed to update to-do');
      }
    } catch (error) {
      toast.error('Failed to update to-do');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleComplete = async (todo) => {
    const newStatus = todo.status === 'completed' ? 'urgent' : 'completed';
    try {
      const res = await fetch(`${API_URL}/api/my-todos/${todo.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success(newStatus === 'completed' ? 'Completed!' : 'Reopened');
        fetchTodos();
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const toggleStar = async (todo) => {
    try {
      const res = await fetch(`${API_URL}/api/my-todos/${todo.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ starred: !todo.starred })
      });

      if (res.ok) {
        fetchTodos();
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/my-todos/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Deleted');
        fetchTodos();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openEditModal = (todo) => {
    setEditingTodo(todo);
    setFormData({
      title: todo.title,
      description: todo.description || '',
      status: todo.status,
      due_date: todo.due_date || '',
      due_time: todo.due_time || '',
      starred: todo.starred
    });
    setShowEditModal(true);
  };

  const openReminderModal = (todo) => {
    setReminderTodo(todo);
    // Pre-fill: existing remind_at if set, else today + 9am
    if (todo.remind_at) {
      // Trim to "YYYY-MM-DDTHH:MM" for datetime-local input
      setReminderAt(todo.remind_at.slice(0, 16));
    } else {
      const d = new Date();
      d.setMinutes(0, 0, 0);
      d.setHours(d.getHours() + 1);
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString().slice(0, 16);
      setReminderAt(local);
    }
  };

  const handleSetReminder = async () => {
    if (!reminderTodo || !reminderAt) {
      toast.error('Pick a date and time first');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/my-todos/${reminderTodo.id}/reminder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ remind_at: `${reminderAt}:00` }),
      });
      if (res.ok) {
        toast.success('Reminder scheduled — will appear on your calendar');
        setReminderTodo(null);
        setReminderAt('');
        fetchTodos();
      } else {
        toast.error('Failed to schedule reminder');
      }
    } catch (err) {
      toast.error('Failed to schedule reminder');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearReminder = async (todo) => {
    try {
      const res = await fetch(`${API_URL}/api/my-todos/${todo.id}/reminder`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Reminder cleared');
        fetchTodos();
      }
    } catch (err) {
      toast.error('Failed to clear reminder');
    }
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-slate-50 to-gray-100 border-slate-200">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#DC2626]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="dashboard-todo-section">
      {/* Hero Card Only - Compact view */}
      <Card className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 text-white overflow-hidden">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <Sparkles className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold">My To-Do List</h2>
                <p className="text-white/80 text-sm">
                  {stats.urgent > 0 
                    ? `${stats.urgent} urgent, ${stats.total - (stats.completed || 0) - stats.urgent} more to go`
                    : `${stats.total - (stats.completed || 0)} items remaining`
                  }
                </p>
              </div>
            </div>
            <Button 
              onClick={() => { setFormData({ title: '', description: '', status: 'urgent', due_date: '', due_time: '', starred: false }); setShowAddModal(true); }}
              className="bg-white text-indigo-600 hover:bg-white/90"
              size="sm"
              data-testid="add-todo-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>

          {/* Quick Stats - Clickable filter buttons */}
          <div className="grid grid-cols-4 gap-2 mt-4">
            <button
              onClick={() => { setStatusFilter('all'); setShowListModal(true); }}
              className="text-center p-2 rounded-lg transition-all bg-white/10 hover:bg-white/20"
              data-testid="filter-active"
            >
              <div className="text-xl font-bold">{stats.total - (stats.completed || 0)}</div>
              <div className="text-xs text-white/80">Active</div>
            </button>
            <button
              onClick={() => { setStatusFilter('urgent'); setShowListModal(true); }}
              className="text-center p-2 rounded-lg transition-all bg-white/10 hover:bg-white/20"
              data-testid="filter-urgent"
            >
              <div className="text-xl font-bold">{stats.urgent || 0}</div>
              <div className="text-xs text-white/80">Urgent</div>
            </button>
            <button
              onClick={() => { setStatusFilter('todo'); setShowListModal(true); }}
              className="text-center p-2 rounded-lg transition-all bg-white/10 hover:bg-white/20"
              data-testid="filter-todo"
            >
              <div className="text-xl font-bold">{stats.todo || 0}</div>
              <div className="text-xs text-white/80">To Do</div>
            </button>
            <button
              onClick={() => { setStatusFilter('completed'); setShowListModal(true); }}
              className="text-center p-2 rounded-lg transition-all bg-white/10 hover:bg-white/20"
              data-testid="filter-done"
            >
              <div className="text-xl font-bold">{stats.completed || 0}</div>
              <div className="text-xs text-white/80">Done</div>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* List Modal - Shows filtered items when a stat is clicked */}
      <Dialog open={showListModal} onOpenChange={setShowListModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-indigo-600" />
              {statusFilter === 'all' ? 'Active Tasks' : 
               statusFilter === 'urgent' ? 'Urgent Tasks' :
               statusFilter === 'todo' ? 'To-Do Tasks' : 'Completed Tasks'}
              <Badge variant="secondary" className="ml-2">{filteredTodos.length}</Badge>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-2 py-2">
            {filteredTodos.length > 0 ? (
              filteredTodos.map(todo => (
                <div 
                  key={todo.id}
                  className={`p-3 rounded-lg border ${
                    todo.status === 'completed' ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggleComplete(todo)}
                      className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        todo.status === 'completed' 
                          ? 'bg-green-500 border-green-500 text-white' 
                          : 'border-slate-300 hover:border-green-500'
                      }`}
                      data-testid={`list-toggle-complete-${todo.id}`}
                    >
                      {todo.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-sm ${todo.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                        {todo.title}
                      </p>
                      {todo.description && (
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{todo.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={STATUSES[todo.status]?.lightColor || 'bg-slate-100'} variant="outline">
                          {STATUSES[todo.status]?.label || todo.status}
                        </Badge>
                        {todo.due_date && (
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <CalendarDays className="w-3 h-3" />
                            {new Date(todo.due_date).toLocaleDateString()}
                          </span>
                        )}
                        {todo.remind_at && (
                          <span className="text-xs text-rose-600 flex items-center gap-1" data-testid={`reminder-badge-${todo.id}`}>
                            <Bell className="w-3 h-3" />
                            {new Date(todo.remind_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleStar(todo)}
                        className="p-1 hover:bg-slate-100 rounded"
                      >
                        {todo.starred ? (
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        ) : (
                          <StarOff className="w-4 h-4 text-slate-300" />
                        )}
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 hover:bg-slate-100 rounded">
                            <MoreVertical className="w-4 h-4 text-slate-400" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { openEditModal(todo); setShowListModal(false); }}>
                            <Edit className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openReminderModal(todo)}>
                            <Bell className="w-4 h-4 mr-2" />
                            {todo.remind_at ? 'Edit Reminder' : 'Set Reminder'}
                          </DropdownMenuItem>
                          {todo.remind_at && (
                            <DropdownMenuItem onClick={() => handleClearReminder(todo)}>
                              <BellOff className="w-4 h-4 mr-2" />
                              Clear Reminder
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDelete(todo.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No {statusFilter === 'all' ? 'active' : statusFilter} tasks</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-3">
            <Button 
              onClick={() => { setFormData({ title: '', description: '', status: statusFilter === 'completed' ? 'todo' : statusFilter, due_date: '', due_time: '', starred: false }); setShowAddModal(true); setShowListModal(false); }}
              className="bg-indigo-600 hover:bg-indigo-700 w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder Modal */}
      <Dialog open={!!reminderTodo} onOpenChange={(open) => { if (!open) { setReminderTodo(null); setReminderAt(''); } }}>
        <DialogContent className="max-w-sm" data-testid="set-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-rose-500" />
              Set Reminder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600 line-clamp-2">
              <strong>{reminderTodo?.title}</strong>
            </p>
            <div>
              <Label htmlFor="reminder-at">Remind me at</Label>
              <Input
                id="reminder-at"
                type="datetime-local"
                value={reminderAt}
                onChange={(e) => setReminderAt(e.target.value)}
                className="mt-1"
                data-testid="reminder-datetime-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Will appear on your Calendar at this time and trigger a push notification.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReminderTodo(null); setReminderAt(''); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSetReminder}
              disabled={submitting || !reminderAt}
              className="bg-rose-500 hover:bg-rose-600 text-white"
              data-testid="reminder-save-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Bell className="w-4 h-4 mr-2" />
              Save Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#DC2626]" />
              Add To-Do
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="What needs to be done?"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add more details..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">
                      <span className="flex items-center gap-2">
                        <Zap className="w-3 h-3 text-red-500" /> Urgent
                      </span>
                    </SelectItem>
                    <SelectItem value="todo">
                      <span className="flex items-center gap-2">
                        <ListTodo className="w-3 h-3 text-blue-500" /> To Do
                      </span>
                    </SelectItem>
                    <SelectItem value="on_hold">
                      <span className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-amber-500" /> On Hold
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="mt-1"
                  data-testid="add-todo-due-date"
                />
              </div>
            </div>

            {formData.due_date && (
              <div>
                <Label className="flex items-center gap-1">
                  <Bell className="w-3 h-3" />
                  Due Time (optional — sets a 10-min push reminder)
                </Label>
                <Input
                  type="time"
                  value={formData.due_time}
                  onChange={(e) => setFormData({ ...formData, due_time: e.target.value })}
                  className="mt-1"
                  data-testid="add-todo-due-time"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {formData.due_time
                    ? `You'll get a push notification 10 minutes before ${formData.due_time}.`
                    : "Leave blank for an all-day to-do (reminder at 8:50 AM)."}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={submitting}
              className="bg-[#DC2626] hover:bg-[#B91C1C]"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Add To-Do
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#DC2626]" />
              Edit To-Do
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="What needs to be done?"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add more details..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">
                      <span className="flex items-center gap-2">
                        <Zap className="w-3 h-3 text-red-500" /> Urgent
                      </span>
                    </SelectItem>
                    <SelectItem value="todo">
                      <span className="flex items-center gap-2">
                        <ListTodo className="w-3 h-3 text-blue-500" /> To Do
                      </span>
                    </SelectItem>
                    <SelectItem value="on_hold">
                      <span className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-amber-500" /> On Hold
                      </span>
                    </SelectItem>
                    <SelectItem value="completed">
                      <span className="flex items-center gap-2">
                        <CheckCircle2 className="w-3 h-3 text-green-500" /> Completed
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="mt-1"
                  data-testid="edit-todo-due-date"
                />
              </div>
            </div>

            {formData.due_date && (
              <div>
                <Label className="flex items-center gap-1">
                  <Bell className="w-3 h-3" />
                  Due Time (optional — sets a 10-min push reminder)
                </Label>
                <Input
                  type="time"
                  value={formData.due_time}
                  onChange={(e) => setFormData({ ...formData, due_time: e.target.value })}
                  className="mt-1"
                  data-testid="edit-todo-due-time"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {formData.due_time
                    ? `You'll get a push notification 10 minutes before ${formData.due_time}.`
                    : "Leave blank for an all-day to-do (reminder at 8:50 AM)."}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={submitting}
              className="bg-[#DC2626] hover:bg-[#B91C1C]"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
